import React, { useState, useEffect } from 'react';
import { getApiUrl } from '@/utils/authUtils';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useLanguage } from '@/context/LanguageContext';
import { Flame, Target, Trophy, ChevronRight, Zap, Calendar, Star } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';

const API_URL = getApiUrl();

const QuickStats = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const { isSpanish } = useLanguage();
  const [challengeData, setChallengeData] = useState(null);
  const [goalData, setGoalData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch challenges + goal progress in parallel
        const [chRes, goalRes] = await Promise.all([
          apiFetch('/api/challenges/overview'),
          apiFetch('/api/friends/streak/goal'),
        ]);
        if (!chRes.error && chRes.data) setChallengeData(chRes.data);
        if (!goalRes.error && goalRes.data) setGoalData(goalRes.data);
      } catch (error) {
        console.error('Failed to fetch quick-stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();

    // Refresh goal progress when a phrase view is tracked elsewhere in the
    // app (SubscriptionContext dispatches this on successful track-view).
    const onPhraseViewed = () => {
      apiFetch('/api/friends/streak/goal').then(({ data, error }) => {
        if (!error && data) setGoalData(data);
      });
    };
    window.addEventListener('phrase-viewed', onPhraseViewed);
    return () => window.removeEventListener('phrase-viewed', onPhraseViewed);
  }, [isAuthenticated]);

  // Don't show for non-authenticated users
  if (!isAuthenticated || !user) {
    return null;
  }

  const currentStreak = user.current_streak || 0;
  const longestStreak = user.longest_streak || 0;
  const dailyGoal = goalData?.daily_goal ?? user.streak_goal?.daily_goal ?? 3;
  const todayProgress = goalData?.current_progress ?? 0;
  const goalProgress = Math.min(100, Math.round((todayProgress / Math.max(dailyGoal, 1)) * 100));
  const goalMet = todayProgress >= dailyGoal;

  // Calculate total potential XP from challenges
  const potentialXP = challengeData ? 
    (challengeData.daily?.reward_xp || 0) + 
    (challengeData.weekly?.reward_xp || 0) + 
    (challengeData.monthly?.reward_xp || 0) : 0;

  // Get active challenge count
  const activeChallenges = challengeData ? 
    (challengeData.daily ? 1 : 0) + 
    (challengeData.weekly ? 1 : 0) + 
    (challengeData.monthly ? 1 : 0) : 0;

  if (isLoading) {
    return (
      <div className="container px-4 mb-6">
        <Card className="bg-gradient-to-r from-primary/5 to-purple-500/5 border-primary/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container px-4 mb-6" data-testid="quick-stats-widget">
      <Card className="bg-gradient-to-r from-primary/5 via-purple-500/5 to-pink-500/5 border-primary/20 overflow-hidden">
        <CardContent className="py-4 px-4 sm:px-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm sm:text-base flex items-center gap-2">
              <Zap className="h-4 w-4 text-yellow-500" />
              {isSpanish ? 'Tu Progreso Hoy' : 'Your Progress Today'}
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/profile')}
              className="text-xs"
            >
              {isSpanish ? 'Ver Perfil' : 'View Profile'}
              <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {/* Current Streak */}
            <div 
              className="bg-background/60 rounded-lg p-3 text-center cursor-pointer hover:bg-background/80 transition-colors"
              onClick={() => navigate('/profile')}
              data-testid="streak-stat"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="text-xl sm:text-2xl font-bold text-orange-500">{currentStreak}</span>
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {isSpanish ? 'Racha' : 'Streak'}
              </p>
              {currentStreak > 0 && currentStreak === longestStreak && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span className="text-[9px] text-yellow-600">
                    {isSpanish ? '¡Récord!' : 'Best!'}
                  </span>
                </div>
              )}
            </div>

            {/* Daily Goal Progress */}
            <div 
              className="bg-background/60 rounded-lg p-3 text-center cursor-pointer hover:bg-background/80 transition-colors"
              onClick={() => navigate('/leaderboard')}
              data-testid="goal-stat"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Target className={`h-4 w-4 ${goalMet ? 'text-green-500' : 'text-blue-500'}`} />
                <span className={`text-xl sm:text-2xl font-bold ${goalMet ? 'text-green-500' : 'text-blue-500'}`}>
                  {todayProgress}/{dailyGoal}
                </span>
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {isSpanish ? 'Meta Diaria' : 'Daily Goal'}
              </p>
              <Progress 
                value={goalProgress} 
                className="h-1.5 mt-2" 
              />
              {goalMet && (
                <span className="text-[9px] text-green-600 mt-1 block">
                  ✓ {isSpanish ? '¡Completado!' : 'Complete!'}
                </span>
              )}
            </div>

            {/* Active Challenges */}
            <div 
              className="bg-background/60 rounded-lg p-3 text-center cursor-pointer hover:bg-background/80 transition-colors"
              onClick={() => navigate('/challenges')}
              data-testid="challenges-stat"
            >
              <div className="flex items-center justify-center gap-1 mb-1">
                <Trophy className="h-4 w-4 text-purple-500" />
                <span className="text-xl sm:text-2xl font-bold text-purple-500">{activeChallenges}</span>
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {isSpanish ? 'Desafíos' : 'Challenges'}
              </p>
              {potentialXP > 0 && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Zap className="h-3 w-3 text-yellow-500" />
                  <span className="text-[9px] text-yellow-600">+{potentialXP} XP</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Action Button */}
          {!goalMet && (
            <div className="mt-4 pt-3 border-t border-border/50">
              <Button 
                size="sm" 
                className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                onClick={() => navigate('/library')}
                data-testid="continue-learning-btn"
              >
                <Calendar className="h-4 w-4 mr-2" />
                {isSpanish 
                  ? `Ver ${dailyGoal - todayProgress} más para completar la meta` 
                  : `View ${dailyGoal - todayProgress} more to complete goal`}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickStats;
